import { useAuthStore } from "@/store/authStore";

const API_URL = import.meta.env.VITE_API_URL;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setAuth, logout } = useAuthStore.getState();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      setAuth(data.access_token, data.refresh_token, data.token_type);
      return data.access_token;
    } else {
      logout();
      return null;
    }
  } catch (error) {
    logout();
    return null;
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const { accessToken, tokenType } = useAuthStore.getState();
  const headers: HeadersInit = { ...options?.headers };

  if (accessToken && !headers["Authorization" as keyof HeadersInit]) {
    (headers as any)["Authorization"] =
      `${tokenType || "Bearer"} ${accessToken}`;
  }

  // Only set Content-Type to application/json if it's not already set
  // and the body is not FormData or URLSearchParams
  if (
    !headers["Content-Type" as keyof HeadersInit] &&
    !(options?.body instanceof FormData) &&
    !(options?.body instanceof URLSearchParams)
  ) {
    (headers as any)["Content-Type"] = "application/json";
  }

  let response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    const newAccessToken = await refreshAccessToken();
    if (newAccessToken) {
      const { tokenType: newTokenType } = useAuthStore.getState();
      (headers as any)["Authorization"] =
        `${newTokenType || "Bearer"} ${newAccessToken}`;
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Something went wrong");
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const apiClient = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: "GET" }),
  post: <T>(endpoint: string, body: any, options?: RequestInit) => {
    let finalBody = body;
    if (body instanceof URLSearchParams || body instanceof FormData) {
      finalBody = body;
    } else if (typeof body === "object") {
      finalBody = JSON.stringify(body);
    }

    return request<T>(endpoint, {
      ...options,
      method: "POST",
      body: finalBody,
    });
  },
  put: <T>(endpoint: string, body: any, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: "DELETE" }),
  upload: <T>(endpoint: string, file: File, options?: RequestInit) => {
    const formData = new FormData();
    formData.append("file", file);
    return request<T>(endpoint, {
      ...options,
      method: "POST",
      body: formData,
      headers: {
        ...options?.headers,
      },
    });
  },
};
